/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable  react/no-array-index-key */
import React, { RefObject, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import Image from 'src/components/ui/Image/Image';
import { useQueryClient } from '@tanstack/react-query';

import { useTranslation } from 'react-i18next';
import { Control, FieldErrors, UseFormSetValue, useWatch } from 'react-hook-form';
import { ControlledInput } from 'src/components/ui/Input/Input';
import { Product, useProducts } from 'src/services/productsNew';
import ContainerOverlay from 'src/components/ui/ContainerOverlay/ContainerOverlay';
import ContainerHead from 'src/components/ui/ContainerOverlay/ContainerHead/ContainerHead';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import ContainerBody from 'src/components/ui/ContainerOverlay/ContainerBody/ContainerBody';
import { formValuesToLot } from 'src/components/layouts/ProductOverlay/ProductFormTypes';
import { useLot } from 'src/services/lots';
import { toast } from 'react-toastify';
import TablePagination from 'src/components/ui/TablePagination/TablePagination';
import Select, { Option } from 'src/components/ui/Select/Select';
import { getPaginationOptions } from 'src/utils/general';
import Icon from 'src/components/ui/Icon/Icon';
import { InboundFormDataStepValues } from './InboundFormContext';
import styles from './InboundPackageTable.module.scss';
import { ProductLotEdit, ProductLotEditRef } from './ProductLotEdit';

const Header: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="typo-text-12 text-faded flex gap-4">{children}</div>
);

const NameHeader: React.FC = () => {
  const { t } = useTranslation();
  return <Header>{t(`inbound.name`)}</Header>;
};
const AmountHeader: React.FC = () => {
  const { t } = useTranslation();
  return <Header>{t(`inbound.amount`)}</Header>;
};
// const LabelingHeader: React.FC = () => {
//   const { t } = useTranslation();
//   return (
//     <Header>
//       {t(`inbound.self_labeling`)}
//       <Tooltip title="Self-labeling" />
//     </Header>
//   );
// };
const LotHeader: React.FC = () => {
  const { t } = useTranslation();
  return <Header>{t(`inbound.lot`)}</Header>;
};
// const QualityChecksHeader: React.FC = () => {
//   const { t } = useTranslation();
//   return <Header>{t(`inbound.quality_checks`)}</Header>;
// };

interface LotOverlayProps {
  productId: number;
  show: boolean;
  onClose: () => void;
  onSelectedLotChanged: (lotId: number | null) => void;
  defaultSelectedIds?: number[];
  /** When false, hides the "create lot" action. Lots may only be created in the inbound flow. */
  allowCreate?: boolean;
}

const LotOverlay: React.FC<LotOverlayProps> = ({
  productId,
  onClose,
  show,
  onSelectedLotChanged,
  defaultSelectedIds,
  allowCreate = true,
}) => {
  const mainRef = React.useRef<HTMLDivElement>(null);
  const formRef = useRef<ProductLotEditRef>(null);
  const { createLotAsync, deleteLotAsync, updateLotAsync } = useLot(productId);

  const [lotsValid, setLotsValid] = useState(false);
  const [hasLotIdSelected, setHasLotIdSelected] = useState(false);

  const { t } = useTranslation();

  return (
    <ContainerOverlay className={(styles.root, 'overflow-y-auto')} isExpanded={show} ref={mainRef}>
      <div role="none" onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
        <div className="w-full sticky top-0 z-10">
          <ContainerHead>
            <Button
              icon="keyboard-tab-right"
              onClick={e => {
                e.stopPropagation();
                formRef.current?.reset();
                onClose();
              }}
              tone={ButtonToneProps.DESTRUCTIVE_TRANSPARENT}
            />

            <h3 className="typo-text-16--bold text-full truncate">{t('lots.select_lot')}</h3>

            <div className="ml-auto flex gap-16 items-center">
              <Button
                disabled={!hasLotIdSelected || !lotsValid}
                onClick={e => {
                  e.stopPropagation();
                  formRef.current?.triggerValidation(async values => {
                    if (!values) {
                      return;
                    }
                    const { lotsToCreate, lotsToDelete, lotsToUpdate, lotsSelectedIds } = values;
                    const deletePromises = lotsToDelete.map(lotId =>
                      deleteLotAsync({
                        params: {
                          path: {
                            lot_id: lotId,
                          },
                        },
                      }),
                    );
                    const updatePromises = lotsToUpdate.map(lot =>
                      updateLotAsync({
                        params: {
                          path: {
                            lot_id: lot.id,
                          },
                        },
                        body: formValuesToLot(lot),
                      }),
                    );

                    try {
                      // A "create new lot" left with a blank name isn't a real lot — the merchant
                      // may not know it yet. Never persist it; treat it the same as "no lot".
                      const lotsToActuallyCreate = lotsToCreate.filter(lot => lot.name.trim() !== '');
                      const skippedCreateIds = new Set(
                        lotsToCreate.filter(lot => lot.name.trim() === '').map(lot => lot.id),
                      );

                      // the client creates an entity with a random ID that the server will replace with a real one
                      // In case the user creates a new entity, and also has selected it, the lotsToCreate ID will be
                      // a client-side fake ID and needs to be replaced with the real one after the create API call
                      // went through
                      const clientSideCreateIds = lotsToActuallyCreate.map(lot => lot.id);
                      const createPromises = lotsToActuallyCreate.map(lot =>
                        createLotAsync({
                          params: {
                            path: {
                              product_id: productId,
                            },
                          },
                          body: formValuesToLot(lot),
                        }),
                      );
                      const createdLotsIds = (await Promise.all(createPromises)).map(lot => lot.id);
                      const realSelectedIds = lotsSelectedIds
                        .filter(id => !skippedCreateIds.has(id))
                        .map(id =>
                          clientSideCreateIds.includes(id) ? createdLotsIds[clientSideCreateIds.indexOf(id)] : id,
                        );

                      await Promise.all([...deletePromises, ...updatePromises]);
                      onSelectedLotChanged((realSelectedIds[0] as any) ?? null);
                      if (lotsToActuallyCreate.length || lotsToDelete.length || lotsToUpdate.length) {
                        toast.success(t('lots.success_updating_lots'));
                      }
                    } catch (e) {
                      console.error(e);
                      toast.error(t('lots.error_updating_lots'));
                    }
                  });
                }}
                title={t('lots.save')}
                tone={ButtonToneProps.PRIMARY_BLUE}
              />
              <Button
                icon="close"
                onClick={e => {
                  e.stopPropagation();
                  formRef.current?.reset();
                  onClose();
                }}
                tone={ButtonToneProps.SECONDARY}
              />
            </div>
          </ContainerHead>
        </div>
        <ContainerBody>
          <ProductLotEdit
            ref={formRef}
            productId={productId}
            allowCreate={allowCreate}
            defaultSelectedIds={defaultSelectedIds}
            onDirectSelect={lotId => {
              // Wählen übernimmt die Charge sofort — kein separater Speichern-Schritt,
              // da bestehende Chargen hier nicht editierbar sind (keine offenen Änderungen).
              onSelectedLotChanged(lotId);
            }}
            onValidityChange={valid => {
              setLotsValid(valid);
            }}
            onSelectedIdsChange={selectedIds => {
              setHasLotIdSelected(selectedIds.size > 0);
            }}
          />
        </ContainerBody>
      </div>
    </ContainerOverlay>
  );
};

export const LotCell: React.FC<{
  productId: number;
  product?: Product;
  lotId: number | null;
  setLotId: (lotId: number | null) => void;
  readOnly?: boolean;
  error?: boolean;
  errorMessage?: string;
  /** When false, hides the "create lot" action in the lot overlay. Lots may only be created in the inbound flow. */
  allowCreate?: boolean;
}> = ({ lotId, productId, product, setLotId, readOnly, error, errorMessage, allowCreate = true }) => {
  const [showSelectLotOverlay, setShowSelectLotOverlay] = useState(false);

  const { lots } = useLot(productId && productId > 0 ? productId : 0);
  const queryClient = useQueryClient();
  const lotName = lots?.find(lot => lot.id === lotId)?.name ?? null;
  const { t } = useTranslation();

  const lotUnselectedLabel = readOnly ? t(`inbound.no_lot_selected`) : t(`inbound.select_lot`);
  const label = lotId === null ? lotUnselectedLabel : lotName;
  const showButton = product && product.tracking !== 'none';

  if (!showButton) {
    return undefined;
  }

  return (
    <>
      {showSelectLotOverlay && (
        <LotOverlay
          productId={productId}
          allowCreate={allowCreate}
          show={showSelectLotOverlay}
          onClose={() => {
            setShowSelectLotOverlay(false);
          }}
          onSelectedLotChanged={lotId => {
            setLotId(lotId);
            setShowSelectLotOverlay(false);

            if (lotId && productId && productId > 0) {
              queryClient.invalidateQueries({
                queryKey: ['get', '/products/{product_id}/lots/', { path: { product_id: productId } }],
              });
            }
          }}
          defaultSelectedIds={lotId === null ? [] : [lotId]}
        />
      )}
      <Button
        onClick={e => {
          e.stopPropagation();
          if (productId && productId > 0) {
            setShowSelectLotOverlay(true);
          } else {
            console.error('Cannot open lot overlay: invalid productId', productId);
          }
        }}
        disabled={readOnly || !productId || productId <= 0}
        title={label || t('inbound.select_lot')}
        tone={ButtonToneProps.SECONDARY}
      />
      {error && errorMessage && (
        <div className="flex align-center gap-x-4 mt-8 status-error-full">
          <Icon name="error" width={16} height={16} />
          <p className="typo-text-12--bold">{errorMessage}</p>
        </div>
      )}
    </>
  );
};

const ProductCell: React.FC<{ product?: Product }> = ({ product }) => {
  return (
    <div className="flex flex-col gap-4 min-w-48">
      {product && (
        <>
          <span className="typo-text-14--bold flex items-center gap-4">{product?.name}</span>
          <span className="typo-text-12 text-faded">
            {product?.article_number as any} | {product?.barcode as any}
          </span>
        </>
      )}
    </div>
  );
};

// interface QualityCheckOverlayProps {
//   productId: number;
//   show: boolean;
//   onClose: () => void;
//   onSelectedQualityChecksChanged: (qualityCheckIds: number[] | null) => void;
//   defaultSelectedIds?: number[];
// }

// const QualityCheckOverlay: React.FC<QualityCheckOverlayProps> = ({
//   productId,
//   onClose,
//   show,
//   onSelectedQualityChecksChanged,
//   defaultSelectedIds,
// }) => {
//   const mainRef = React.useRef<HTMLDivElement>(null);
//   const formRef = useRef<ProductQualityCheckEditRef>(null);
//   const { createQualityCheckAsync, deleteQualityCheckAsync, updateQualityCheckAsync } = useQualityCheckMutations();
//
//   const { t } = useTranslation();
//
//   useOutsideClick(mainRef, e => {
//     const event = e as MouseEvent;
//     if (mainRef.current && !mainRef.current.contains(event.target as Node)) {
//       formRef.current?.reset();
//       onClose();
//     }
//   });
//
//   return (
//     <ContainerOverlay className={styles.root} isExpanded={show} ref={mainRef}>
//       <ContainerHead>
//         <Button
//           icon="keyboard-tab-right"
//           onClick={() => {
//             formRef.current?.reset();
//             onClose();
//           }}
//           tone={ButtonToneProps.DESTRUCTIVE_TRANSPARENT}
//         />
//
//         <h3 className="typo-text-16--bold text-full truncate">{t('quality.select_checks')}</h3>
//
//         <div className="ml-auto flex gap-16 items-center">
//           <Button
//             onClick={() => {
//               formRef.current?.triggerValidation(async values => {
//                 if (!values) {
//                   // validation failed
//                   return;
//                 }
//                 const {
//                   qualityChecksToCreate,
//                   qualityChecksToDelete,
//                   qualityChecksToUpdate,
//                   qualityChecksSelectedIds,
//                 } = values;
//
//                 try {
//                   const deletePromises = qualityChecksToDelete.map(checkId =>
//                     deleteQualityCheckAsync({
//                       params: {
//                         path: {
//                           quality_check_id: checkId,
//                         },
//                       },
//                     }),
//                   );
//                   const updatePromises = qualityChecksToUpdate.map(check =>
//                     updateQualityCheckAsync({
//                       params: {
//                         path: {
//                           quality_check_id: check.id,
//                         },
//                       },
//                       body: check,
//                     }),
//                   );
//                   // the client creates an entity with a random ID that the server will replace with a real one
//                   // In case the user creates a new entity, and also has selected it, the lotsToCreate ID will be
//                   // a client-side fake ID and needs to be replaced with the real one after the create API call
//                   // went through
//                   const clientSideCreateIds = qualityChecksToCreate.map(check => check.id);
//                   const createPromises = formValuesToQualityCheckIns(qualityChecksToCreate).map(check =>
//                     createQualityCheckAsync({
//                       params: {
//                         path: {
//                           product_id: productId,
//                         },
//                       },
//                       body: check,
//                     }),
//                   );
//                   const createdLotsIds = (await Promise.all(createPromises)).map(check => check.id);
//                   const realSelectedIds = qualityChecksSelectedIds.map((id, idx) =>
//                     clientSideCreateIds.includes(id) ? createdLotsIds[idx] : id,
//                   );
//                   await Promise.all([...deletePromises, ...updatePromises]);
//                   onSelectedQualityChecksChanged((realSelectedIds as any) ?? null);
//                   onClose();
//                   if (qualityChecksToCreate.length || qualityChecksToDelete.length || qualityChecksToUpdate.length) {
//                     toast.success(t('quality.success_updating_quality_checks'));
//                   }
//                 } catch (e) {
//                   console.error(e);
//                   toast.error(t('lots.error_updating_lots'));
//                   throw e;
//                 }
//               });
//             }}
//             title={t('lots.save')}
//             tone={ButtonToneProps.PRIMARY_BLUE}
//           />
//           <Button
//             icon="close"
//             onClick={() => {
//               formRef.current?.reset();
//               onClose();
//             }}
//             tone={ButtonToneProps.SECONDARY}
//           />
//         </div>
//       </ContainerHead>
//       <ContainerBody>
//         <ProductQualityCheckEdit ref={formRef} productId={productId} defaultSelectedIds={defaultSelectedIds} />
//       </ContainerBody>
//     </ContainerOverlay>
//   );
// };
//
// const QualityChecksCell: React.FC<{
//   qualityCheckIds: number[];
//   productId: number;
//   setQualityCheckIds: (qualityCheckIds: number[]) => void;
//   readOnly?: boolean;
// }> = ({ qualityCheckIds, productId, setQualityCheckIds, readOnly }) => {
//   const [showSelectQualityChecks, setShowSelectQualityChecks] = useState(false);
//   const mainRef = React.useRef<HTMLDivElement>(null);
//
//   useOutsideClick(mainRef, e => {
//     const event = e as MouseEvent;
//     if (mainRef.current && !mainRef.current.contains(event.target as Node)) {
//       setShowSelectQualityChecks(false);
//     }
//   });
//
//   useEffect(() => {
//     if (mainRef.current) {
//       setShowSelectQualityChecks(true);
//     }
//   }, [mainRef]);
//
//   const { t } = useTranslation();
//   return (
//     <>
//       {showSelectQualityChecks && (
//         <QualityCheckOverlay
//           show={showSelectQualityChecks}
//           onClose={() => setShowSelectQualityChecks(false)}
//           productId={productId}
//           onSelectedQualityChecksChanged={qualityCheckIds => {
//             setQualityCheckIds(qualityCheckIds ?? []);
//           }}
//           defaultSelectedIds={qualityCheckIds}
//         />
//       )}
//       <button
//         type="button"
//         className="typo-text-14 whitespace-nowrap"
//         onClick={() => setShowSelectQualityChecks(true)}
//         disabled={readOnly}>
//         {t('inbound.n_quality_checks', { count: qualityCheckIds.length })}
//       </button>
//     </>
//   );
// };

const AmountCell: React.FC<{
  packageIndex: number;
  rowIndex: number;
  control: Control<InboundFormDataStepValues>;
  errors: FieldErrors<InboundFormDataStepValues>;
  readOnly?: boolean;
}> = ({ rowIndex, packageIndex, control, errors, readOnly }) => {
  const fieldName = `packages.${packageIndex}.lines.${rowIndex}.quantity`;
  const fieldError = errors?.packages?.[packageIndex]?.lines?.[rowIndex]?.quantity;
  const { t } = useTranslation();

  return (
    <div>
      <ControlledInput
        min={1}
        max={999999999}
        control={control}
        name={fieldName}
        placeholder={t('inbound.amount')}
        error={!!fieldError}
        errorMessage={fieldError?.message}
        isInfoDisplay
        className="col-span-2"
        type="number"
        maxLength={60}
        disabled={readOnly}
      />
    </div>
  );
};

const ActionsCell: React.FC<{
  onDeleteClick?: () => void;
  readOnly?: boolean;
}> = ({ onDeleteClick, readOnly }) => {
  return (
    <div className="flex gap-4 justify-end">
      {!readOnly && (
        <Button
          icon="delete"
          tone={ButtonToneProps.DESTRUCTIVE_TRANSPARENT}
          ariaLabel="Delete"
          onClick={onDeleteClick}
        />
      )}
    </div>
  );
};

export interface InboundPackageTableProps {
  containerRef?: RefObject<HTMLDivElement>;
  packageIndex: number;
  control: Control<InboundFormDataStepValues>;
  errors: FieldErrors<InboundFormDataStepValues>;
  linesError?: boolean;
  setValue: UseFormSetValue<InboundFormDataStepValues>;
  onAddProductClick?: () => void;
  readOnly?: boolean;
}

const InboundPackageTable: React.FC<InboundPackageTableProps> = ({
  containerRef,
  packageIndex,
  control,
  errors,
  setValue,
  onAddProductClick,
  linesError,
  readOnly,
}) => {
  const allLines = useWatch({ control, name: `packages.${packageIndex}.lines` }) || [];
  const packageData = useWatch({ control, name: `packages.${packageIndex}` }) || {};
  const { t } = useTranslation();
  const options = getPaginationOptions();
  const DEFAULT_PAGE = 1;
  const DEFAULT_LIMIT = 10;

  useEffect(() => {
    if (!packageData.page) {
      setValue(`packages.${packageIndex}.page`, DEFAULT_PAGE);
    }
    if (!packageData.limit) {
      setValue(`packages.${packageIndex}.limit`, DEFAULT_LIMIT);
    }

    const currentPage = packageData.page || DEFAULT_PAGE;
    const itemsPerPage = packageData.limit || DEFAULT_LIMIT;
    const totalPages = Math.ceil(allLines.length / itemsPerPage);

    if (totalPages > 0 && currentPage > totalPages) {
      setValue(`packages.${packageIndex}.page`, DEFAULT_PAGE);
    }
  }, [packageData, packageIndex, setValue, allLines.length]);

  const currentPage = packageData.page || DEFAULT_PAGE;
  const itemsPerPage = packageData.limit || DEFAULT_LIMIT;
  const totalPages = Math.ceil(allLines.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const currentLines = allLines.slice(startIndex, endIndex);

  const uniqueProductIds = Array.from(new Set(allLines.map(line => line.productId)));
  const selectedProducts = useProducts(uniqueProductIds)
    .map(product => product.data)
    .map(r => r?.data);

  const handlePerPageUpdate = (selectedOption: Option | null) => {
    if (selectedOption) {
      const newLimit = Number(selectedOption.value);
      setValue(`packages.${packageIndex}.page`, DEFAULT_PAGE);
      setValue(`packages.${packageIndex}.limit`, newLimit);
    }
  };

  const handleTablePaginationClick = (direction: 'previous' | 'next') => {
    let newPage = direction === 'next' ? currentPage + 1 : currentPage - 1;
    newPage = Math.max(1, Math.min(newPage, totalPages));
    setValue(`packages.${packageIndex}.page`, newPage);
    containerRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const cellClasses = `py-12 px-16`;

  const renderPagination = () => {
    if (!allLines.length) return null;

    return (
      <>
        <div className="flex gap-x-10 flex-1 items-center">
          <div>
            <Select
              onChange={handlePerPageUpdate}
              options={options}
              defaultValue={options.find(opt => opt.value === String(itemsPerPage)) || options[1]}
              menuPlacement="auto"
            />
          </div>
          <div className="typo-text-12 text-faded">{t('general.per_page')}</div>
        </div>
        <div className="ml-auto">
          <div className="w-full my-20 flex items-center">
            <div className="ml-auto">
              <TablePagination
                maxPage={totalPages}
                onPaginationClick={handleTablePaginationClick}
                currentPage={currentPage - 1}
              />
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div ref={containerRef} className={classNames(styles.root)}>
      <div className={styles.tableWrapper}>
        <table className="w-full">
          <thead className={styles.tableHeader}>
            <tr>
              <th className={cellClasses}>
                <NameHeader />
              </th>
              {/* <th className={cellClasses}> */}
              {/*  <QualityChecksHeader /> */}
              {/* </th> */}
              <th className={cellClasses}>
                <AmountHeader />
              </th>
              <th className={cellClasses}>
                <LotHeader />
              </th>
              {/* <th className={cellClasses}> */}
              {/*  <div className="flex justify-center"> */}
              {/*    <LabelingHeader /> */}
              {/*  </div> */}
              {/* </th> */}
            </tr>
          </thead>
          <tbody>
            {currentLines.map((line, index) => {
              const product = selectedProducts.find(p => p?.id === line.productId);
              return (
                <tr key={startIndex + index}>
                  <td className={cellClasses}>
                    <ProductCell product={product} />
                  </td>
                  {/* <td className={cellClasses}> */}
                  {/*  <QualityChecksCell */}
                  {/*    qualityCheckIds={line.appliedQualityChecks} */}
                  {/*    productId={line.productId} */}
                  {/*    setQualityCheckIds={(qualityCheckIds: number[]) => { */}
                  {/*      setValue( */}
                  {/*        `packages.${packageIndex}.lines.${startIndex + index}.appliedQualityChecks`, */}
                  {/*        qualityCheckIds, */}
                  {/*      ); */}
                  {/*    }} */}
                  {/*    readOnly={readOnly} */}
                  {/*  /> */}
                  {/* </td> */}
                  <td className={cellClasses}>
                    <AmountCell
                      packageIndex={packageIndex}
                      rowIndex={startIndex + index}
                      control={control}
                      errors={errors}
                      readOnly={readOnly}
                    />
                  </td>
                  <td className={cellClasses}>
                    <LotCell
                      lotId={line.lotId}
                      productId={line.productId}
                      product={product!}
                      setLotId={lotId => {
                        setValue(`packages.${packageIndex}.lines.${startIndex + index}.lotId`, lotId);
                      }}
                      readOnly={readOnly}
                      error={!!errors?.packages?.[packageIndex]?.lines?.[startIndex + index]?.lotId}
                      errorMessage={errors?.packages?.[packageIndex]?.lines?.[startIndex + index]?.lotId?.message}
                    />
                  </td>
                  {/* <td className={cellClasses}> */}
                  {/*  <div className="flex justify-center items-center pr-32"> */}
                  {/*    <Checkbox */}
                  {/*      checked={line.labeling === 'customer'} */}
                  {/*      onChange={e => { */}
                  {/*        const newVal = e.target.checked ? 'customer' : 'weship'; */}
                  {/*        setValue(`packages.${packageIndex}.lines.${startIndex + index}.labeling`, newVal); */}
                  {/*      }} */}
                  {/*      disabled={readOnly} */}
                  {/*    /> */}
                  {/*  </div> */}
                  {/* </td> */}
                  {!readOnly && (
                    <td className={cellClasses}>
                      <ActionsCell
                        onDeleteClick={() => {
                          const newLines = [...allLines];
                          newLines.splice(startIndex + index, 1);
                          setValue(`packages.${packageIndex}.lines`, newLines);
                        }}
                        readOnly={readOnly}
                      />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>

        {!allLines.length && (
          <button
            disabled={readOnly}
            type="button"
            className={classNames('w-full flex flex-col items-center justify-center py-48 gap-24', {
              'surface-elevated': !linesError,
              'border-error': linesError,
              'status-error-surface': linesError,
              'status-error-full': linesError,
            })}
            onClick={onAddProductClick}>
            <Image
              className="w-[84px]"
              imageUrl="/assets/illustrations/empty-box.png"
              alt=""
              fallbackComponent={<div className="surface-skeleton-faded w-full h-full rounded-sm" />}
            />
            <span className="typo-title-24 pb-12">{t('inbound.no_product_added')}</span>
          </button>
        )}
      </div>
      <div className="w-full my-20 flex items-center">{renderPagination()}</div>
    </div>
  );
};

export default InboundPackageTable;
